import React, { Component, createRef, ReactNode, RefObject } from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import CloseIcon from '@material-ui/icons/Close';
import SearchCalendarImage from '../../../images/front/search/search_calendar.png';
import moment from 'moment';
import { DayPickerRangeController as DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

// anyで定義したくないが、型が通らないのでanyで定義している
// おそらく、stringとnumberの型違いが原因である可能性が高い
const styles = (theme): any => ({
  form: (props) => ({
    position: 'relative',
    width: props.width || 'auto',
    color: props.color || 'unset',

    '& > .MuiFormLabel-root': {
      textIndent: '30',
    },
    '& > div > .DayPicker': {
      position: 'absolute',
      top: '40px',
      left: props.left || '-200px',
      zIndex: '15',
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
        top: '0px',
        left: '0px',
        right: '0px',
        margin: 'auto',
        zIndex: '1100',
      },
    },
    '& > div > .DayPicker > div > div >.DayPicker_focusRegion > .DayPickerNavigation':
      {
        [theme.breakpoints.down('sm')]: {
          position: 'fixed',
        },
      },
    '&, .CalendarDay': {
      border: 'none',
      borderRadius: '50%',
    },
    '&, .CalendarDay__selected': {
      background: '#FF6E2E',
    },
    '&, .CalendarDay__selected_span, .CalendarDay__selected_span:active, .CalendarDay__selected_span:hover':
      {
        background: '#fb9060',
      },
    '& > .CalendarDay__hovered_span, .CalendarDay__hovered_span, .CalendarDay__hovered_span:hover':
      {
        color: '#fff',
        background: '#fbac89',
      },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  }),
  label: (props) => ({
    display: 'inline-flex',
    color: props.color || 'unset',
  }),
  moveLabel: (props) => ({
    display: 'inline-flex',
    color: props.color || 'unset',
    transform: 'translate(14px, -6px) scale(0.75) !important',
  }),
  legendLabel: {
    position: 'absolute',
    padding: '0',
    fontSize: '.9rem',
    fontWeight: 400,
    lineHeight: '1',
    width: 'auto',
    transform: 'translate(14px, 12px) scale(1)',
  },
  legendMove: {
    position: 'absolute',
    padding: '0',
    fontSize: '.9rem',
    fontWeight: 400,
    lineHeight: '1',
    width: 'auto',
    background: '#FFF',
    transform: 'translate(-11px, -28px) scale(0.75) !important',
    [theme.breakpoints.down('sm')]: {
      transform: 'translate(5px, -28px) scale(0.75) !important',
    },
  },
  formInr: {
    visibility: 'hidden',
  },
  startDate: {
    position: 'absolute',
    left: '30px',
    top: '8px',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      left: '20px',
    },
  },
  endDate: (props) => ({
    position: 'absolute',
    left: '150px',
    top: '8px',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      display: props.withoutTrunc ? 'unset' : 'none',
      left: props.withoutTrunc ? '136px' : '150px',
    },
  }),
  arrowIcon: (props) => ({
    position: 'absolute',
    top: '0px',
    right: '-23px',
    [theme.breakpoints.down('sm')]: {
      display: props.withoutTrunc ? 'unset' : 'none',
    },
  }),
  calendarIcon: {
    width: '16%',
    height: '5%',
    padding: '0px 10px 0 5px',
    [theme.breakpoints.down('xs')]: {
      width: '40%',
    },
  },
  formBk: {
    position: 'relative',
    overflow: 'hidden',
  },
  formBkInr: (props) => ({
    borderRadius: 25,
    backgroundColor: theme.palette.common.white,
    textAlign: 'left',
    borderColor: props.color || '#FF6E2E',
    borderStyle: 'solid',
    borderWidth: '1px',
    paddingTop: '19px',
    paddingBottom: '19px',
    cursor: 'pointer',
    '&:hover': {
      borderColor: props.color || '#ed936b',
    },
    [theme.breakpoints.up('sm')]: {
      minWidth: 280,
    },
  }),
  dateRangeBk: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    top: '0px',
    left: '0px',
    zIndex: 14,
  },
  close: {
    position: 'fixed',
    right: '10px',
    top: '10px',
    zIndex: 1101,
  },
  prohibition: {
    position: 'absolute',
    left: '115px',
    top: '8px',
    zIndex: 1,
  },
});

const dateFormat = 'YYYY-MM-DD';

export type Props = {
  classes: { [key: string]: string };
  startDate: moment.MomentInput;
  endDate: moment.MomentInput;
  onChange: (data: {
    startDate: moment.MomentInput;
    endDate: moment.MomentInput;
  }) => void;
  withPortal: boolean;
  withoutTrunc?: boolean;
  label: ReactNode;
  width?: string;
  color?: string;
  left?: string;
};

export type State = {
  startDate: moment.MomentInput;
  endDate: moment.MomentInput;
  focusedInput: string;
  display: boolean;
};

class ScheduleSelect extends Component<Props, State> {
  readonly scheduleRef: RefObject<HTMLDivElement>;
  readonly dateRangeBkRef: RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);

    this.state = {
      startDate: props.startDate ? moment(props.startDate) : null,
      endDate: props.endDate ? moment(props.endDate) : null,
      focusedInput: 'startDate',
      display: false,
    };

    this.scheduleRef = createRef();
    this.dateRangeBkRef = createRef();
    this.handleClickEvent = this.handleClickEvent.bind(this);
    this.onDatesChange = this.onDatesChange.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickEvent);
  }

  componentDidUpdate({ startDate: prevStartDate, endDate: prevEndDate }) {
    const { startDate: currentStartDate, endDate: currentEndDate } = this.props;

    if (prevStartDate !== currentStartDate || prevEndDate !== currentEndDate) {
      this.setState({
        startDate: !!currentStartDate ? moment(currentStartDate) : null,
        endDate: !!currentEndDate ? moment(currentEndDate) : null,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickEvent);
  }

  handleClickEvent(event) {
    if (
      this.scheduleRef &&
      this.scheduleRef.current &&
      this.scheduleRef.current.contains(event.target)
    ) {
      this.setState({ display: true });
    }
    if (
      this.dateRangeBkRef &&
      this.dateRangeBkRef.current &&
      this.dateRangeBkRef.current.contains(event.target)
    ) {
      this.setState({ display: false });
    }
  }

  onDatesChange({ startDate, endDate }) {
    const setStartDate = startDate ? moment(startDate).format(dateFormat) : '';
    const setEndDate = endDate ? moment(endDate).format(dateFormat) : '';

    if (this.state.focusedInput === 'endDate') {
      this.setState({ display: false });
    }

    this.setState({ startDate, endDate });
    this.props.onChange?.({ startDate: setStartDate, endDate: setEndDate });
  }

  onFocusChange = (focusedInput) => {
    this.setState({ focusedInput: focusedInput || 'startDate' });
  };

  render() {
    const { label, classes, withPortal, withoutTrunc } = this.props;
    const { startDate, endDate, focusedInput, display } = this.state;
    const setStartDate = startDate
      ? moment(startDate).format(dateFormat)
      : null;
    const setEndDate = endDate ? moment(endDate).format(dateFormat) : null;
    const orientation = withPortal ? 'vertical' : 'horizontal';

    return (
      <FormControl
        size="small"
        variant="outlined"
        className={classes.form}
        ref={this.scheduleRef}
      >
        <InputLabel
          className={
            display || setStartDate ? classes.moveLabel : classes.label
          }
        >
          <img
            src={SearchCalendarImage}
            className={classes.calendarIcon}
            alt="calendar"
          />
          {label}
        </InputLabel>
        {setStartDate && (
          <Typography className={classes.startDate}>
            {setStartDate}
            <ArrowRightIcon className={classes.arrowIcon} />
          </Typography>
        )}
        {setEndDate && withPortal && !withoutTrunc && (
          <Typography className={classes.prohibition}>...</Typography>
        )}
        {setEndDate && (
          <Typography className={classes.endDate}>{setEndDate}</Typography>
        )}
        <div className={classes.formBk}>
          <div className={classes.formBkInr}>
            <legend
              className={
                display || setStartDate
                  ? classes.legendMove
                  : classes.legendLabel
              }
            >
              <span className={classes.formInr}>
                <img
                  src={SearchCalendarImage}
                  className={classes.calendarIcon}
                  alt="calendar"
                />
                {label}
              </span>
            </legend>
          </div>
        </div>
        {display && (
          <div>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              hideKeyboardShortcutsPanel={true}
              numberOfMonths={2}
              focusedInput={focusedInput}
              onFocusChange={this.onFocusChange}
              onDatesChange={this.onDatesChange}
              withPortal={withPortal}
              orientation={orientation}
            />
            <div className={classes.dateRangeBk} ref={this.dateRangeBkRef} />
          </div>
        )}
        {withPortal && display && (
          <CloseIcon
            className={classes.close}
            onClick={() => {
              this.setState({ display: false });
            }}
          />
        )}
      </FormControl>
    );
  }
}

export default withStyles(styles)(ScheduleSelect);
